import {
  isCompound,
  isOption,
  isValidQuotePrice,
  OrdTypeEnum,
  parseDate,
  PricingModeEnum,
  QuoteStatusEnum,
  toBig,
  toBigWithDefault,
  type QuoteMarket,
  type Security,
} from '@talos/kyoko';
import type Big from 'big.js';
import { isBefore } from 'date-fns';

/**
 * Derive the OrdType for an RFQ accept based on the security type and pricing mode.
 *
 * - Compound / Option securities use OrdType.RFQ
 * - SpreadToFixing pricing mode uses OrdType.RFQ
 * - Everything else defaults to LimitAllIn
 */
export function getOrdTypeForQuote(
  security: Security | undefined,
  pricingMode: PricingModeEnum | undefined
): OrdTypeEnum {
  if (security && isCompound(security)) {
    return OrdTypeEnum.RFQ;
  }

  if (security && isOption(security)) {
    return OrdTypeEnum.RFQ;
  }

  if (pricingMode === PricingModeEnum.SpreadToFixing) {
    return OrdTypeEnum.RFQ;
  }

  return OrdTypeEnum.LimitAllIn;
}

export interface QuoteMarketWithBestBidOffer extends QuoteMarket {
  isBestBid: boolean;
  isBestOffer: boolean;
}

export type RfqBestQuoteBy = 'px' | 'qty';
type BestQuoteSide = 'bid' | 'offer';
type QuoteComparisonResult = -1 | 0 | 1;

interface CalculateBestQuotesParams {
  quotes?: QuoteMarket[] | undefined;
  pricingMode?: PricingModeEnum | undefined;
  onlyActive?: boolean;
  bestBy?: RfqBestQuoteBy;
}

/**
 * Marks the best bid and offer across RFQ market quotes.
 *
 * Best quotes are selected by the requested mode first, then by the opposite
 * mode as a tie-breaker, and finally by earliest transact time. Quantity is
 * always better when higher. Price follows the trading side: higher bid is
 * better for selling, lower offer is better for buying.
 */
export const calculateBestQuotes = ({
  quotes = [],
  pricingMode = undefined,
  onlyActive = true,
  bestBy = 'px',
}: CalculateBestQuotesParams = {}): QuoteMarketWithBestBidOffer[] => {
  const activeQuotes = onlyActive ? quotes.filter(quote => quote.QuoteStatus === QuoteStatusEnum.Open) : quotes;

  let bestBid: QuoteMarket | undefined;
  let bestOffer: QuoteMarket | undefined;
  for (const quote of activeQuotes) {
    const hasValidBid = isValidQuotePrice(quote.BidPx, quote.QuoteStatus, pricingMode);
    const hasValidOffer = isValidQuotePrice(quote.OfferPx, quote.QuoteStatus, pricingMode);

    if (hasValidBid) {
      if (bestBid == null) {
        bestBid = quote;
      } else {
        if (isQuoteBetterThan('bid', quote, bestBid, bestBy)) {
          bestBid = quote;
        }
      }
    }

    if (hasValidOffer) {
      if (bestOffer == null) {
        bestOffer = quote;
      } else {
        if (isQuoteBetterThan('offer', quote, bestOffer, bestBy)) {
          bestOffer = quote;
        }
      }
    }
  }

  return quotes.map(quote => {
    const q: QuoteMarketWithBestBidOffer = { ...quote, isBestBid: false, isBestOffer: false };
    if (quote.Market === bestBid?.Market) {
      q.isBestBid = true;
    }
    if (quote.Market === bestOffer?.Market) {
      q.isBestOffer = true;
    }
    return q;
  });
};

export function getRFQSyntheticCrossSymbol(security: Security | undefined): string | undefined {
  if (!security) {
    return undefined;
  }

  return `${security.BaseCurrency}-USD`;
}

/**
 * Returns true when quote should replace bestQuote for the given side.
 *
 * The requested bestBy mode is compared first, then the other mode is used as a
 * tie-breaker, and earliest TransactTime wins when both values match.
 */
function isQuoteBetterThan(
  side: BestQuoteSide,
  quote: QuoteMarket,
  bestQuote: QuoteMarket,
  bestBy: RfqBestQuoteBy
): boolean {
  const primaryComparison = compareQuoteValue(side, quote, bestQuote, bestBy);
  if (primaryComparison !== 0) {
    return primaryComparison > 0;
  }

  // Size countering can prioritize quantity, so use the non-selected mode as the first tie-breaker.
  const tieBreakBy = bestBy === 'px' ? 'qty' : 'px';
  const tieBreakComparison = compareQuoteValue(side, quote, bestQuote, tieBreakBy);
  if (tieBreakComparison !== 0) {
    return tieBreakComparison > 0;
  }

  // Keep existing behavior stable when economic terms are equivalent.
  const quoteTransactTime = parseDate(quote.TransactTime);
  const bestQuoteTransactTime = parseDate(bestQuote.TransactTime);
  return isBefore(quoteTransactTime, bestQuoteTransactTime);
}

/**
 * Compares either price or quantity for a bid or offer.
 *
 * Positive means quote is better than bestQuote, negative means worse, and zero
 * means equal. Quantities are better when higher; bids are better when higher;
 * offers are better when lower.
 */
function compareQuoteValue(
  side: BestQuoteSide,
  quote: QuoteMarket,
  bestQuote: QuoteMarket,
  bestBy: RfqBestQuoteBy
): QuoteComparisonResult {
  const quoteValue = getQuoteValue(side, quote, bestBy);
  const bestQuoteValue = getQuoteValue(side, bestQuote, bestBy);

  if (quoteValue == null && bestQuoteValue == null) {
    return 0;
  }

  if (quoteValue == null) {
    return -1;
  }

  if (bestQuoteValue == null) {
    return 1;
  }

  if (quoteValue.eq(bestQuoteValue)) {
    return 0;
  }

  if (bestBy === 'qty') {
    return quoteValue.gt(bestQuoteValue) ? 1 : -1;
  }

  // Bid is what we can sell into, offer is what we can buy from.
  const lowerPriceIsBetter = side === 'offer';
  const hasBetterPrice = lowerPriceIsBetter ? quoteValue.lt(bestQuoteValue) : quoteValue.gt(bestQuoteValue);
  return hasBetterPrice ? 1 : -1;
}

function getQuoteValue(side: BestQuoteSide, quote: QuoteMarket, bestBy: RfqBestQuoteBy): Big | undefined {
  if (bestBy === 'px') {
    return toBig(side === 'bid' ? quote.BidPx : quote.OfferPx);
  }

  return toBigWithDefault(side === 'bid' ? quote.BidQty : quote.OfferQty, 0);
}
